import { Link, NavLink, Outlet } from 'react-router-dom';
import { Badge, Container, Logo } from '@/design-system';
import { APP_ROUTES, mayOpen } from '@/app/routes';
import { ROLE_LABELS } from '@/lib/auth/types';
import { useAuth } from '@/lib/auth/useAuth';
import { ThemeToggle } from '@/app/ThemeToggle';
import styles from './AppShell.module.css';

/**
 * The frame around every signed-in page: brand, navigation from the route table
 * (only what this person may open), who they are, and the way out.
 */
export function AppShell() {
  const { me, signOut } = useAuth();
  const routes = me ? APP_ROUTES.filter((route) => !route.hidden && mayOpen(route, me)) : [];

  return (
    <div className={styles.page}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className={styles.header}>
        <Container>
          <div className={styles.headerInner}>
            <Link to="/" className={styles.brand}>
              <Logo height={28} />
            </Link>

            <nav className={styles.tabs} aria-label="Sections">
              {routes.map((route) => (
                <NavLink
                  key={route.path}
                  to={route.path}
                  className={({ isActive }) =>
                    [styles.tab, isActive ? styles.tabActive : ''].filter(Boolean).join(' ')
                  }
                >
                  {route.label}
                </NavLink>
              ))}
            </nav>

            <div className={styles.actions}>
              {me && (
                <div className={styles.identity}>
                  <span className={styles.name}>{me.displayName}</span>
                  <Badge tone={me.canAdminister ? 'accent' : 'neutral'}>{ROLE_LABELS[me.role]}</Badge>
                </div>
              )}
              <ThemeToggle />
              <button
                type="button"
                className={styles.signOut}
                onClick={() => {
                  // A full navigation, not a routed one. RequireSession answers a
                  // missing session with its own redirect to /signin, performed in an
                  // effect after this handler, which would override a routed
                  // navigation. Leaving outright also drops every page's loaded data
                  // rather than leaving it in memory behind a signed-out session.
                  signOut();
                  window.location.assign('/');
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </Container>
      </header>

      <main id="main" className={styles.main}>
        <Container>
          <Outlet />
        </Container>
      </main>
    </div>
  );
}
